/* eslint react-hooks/exhaustive-deps: "warn" */

import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Image } from 'ui/atoms/Image';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TestimonialsSpotlightSection.module.scss';

import type { TestimonialsSpotlightSectionProps } from './TestimonialsSpotlightSection.types';

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const TestimonialsSpotlightSection = ({
  title,
  ctaLabel,
  ctaRoute = '/customers',
  items,
  className,
}: TestimonialsSpotlightSectionProps) => {
  const { t } = useInt();
  const [activeKey, setActiveKey] = useState(items[0]?.key);

  const featured = items.find((item) => item.key === activeKey) ?? items[0];

  if (!featured) {
    return null;
  }

  return (
    <section className={cn(styles.section, className)}>
      <div className={styles.inner}>
        <TitleBlock title={title} align="left" className={styles.titleBlock} />

        <div className={styles.split}>
          <div key={featured.key} className={styles.featured}>
            <Image
              width={120}
              height={34}
              src={featured.logoSrc}
              alt={`${featured.company} logo`}
              className={styles.featuredLogo}
            />

            <p className={styles.quote}>{featured.quote}</p>

            <div className={styles.person}>
              <span className={styles.avatar} style={{ background: featured.color }}>
                {featured.initials}
              </span>
              <span>
                <span className={styles.personName}>{featured.name}</span>
                <span className={styles.personRole}>{featured.role}</span>
              </span>
            </div>

            <Button
              type="blue"
              size="lg"
              href={((route) => (route.startsWith('/') ? route : `/${route}`))(featured.href ?? ctaRoute)}
              className={styles.cta}
            >
              {ctaLabel ?? t('Read full case study')}
            </Button>
          </div>

          <div className={styles.stack}>
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                className={cn(styles.item, item.key === activeKey && styles.itemActive)}
                onClick={() => setActiveKey(item.key)}
              >
                <span className={styles.itemText}>
                  <span className={styles.itemCompany}>{item.company}</span>
                  <span className={styles.itemPerson}>
                    {item.name} · {item.role}
                  </span>
                </span>
                <span className={styles.itemArrow}>
                  <ArrowIcon />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
